import type { ReactNode } from "react";
import { t } from "../i18n/t";
import { cx } from "./klasy";
import { IkonaCheck, IkonaInfo, IkonaOdswiez, IkonaOkKolo, IkonaUsterka, IkonaWifiOff, IkonaWykrzyknik } from "./ikony";
import type { KomponentIkony } from "./ikony";
import { Przycisk, PrzyciskLink } from "./Przyciski";
import "../styles/wspolne.css";

/* ---------- Ładowanie ---------- */
export function Ladowanie({ tekst }: { tekst?: string }) {
  return (
    <div className="iot-ladowanie" role="status" aria-live="polite">
      <span className="iot-wirujace" aria-hidden="true" />
      {tekst ?? t("Wczytuję…")}
    </div>
  );
}

/** Szkielet karty urządzenia na czas pierwszego wczytania */
export function LadowanieKarty() {
  return (
    <div className="iot-karta iot-ladowanie-karty" aria-busy="true" aria-label={t("Wczytuję…")}>
      <div className="iot-ladowanie-karty__naglowek">
        <span className="iot-szkielet" style={{ width: "3rem", height: "3rem" }} />
        <span className="iot-rozpycha iot-stos">
          <span className="iot-szkielet" style={{ height: "1.125rem", width: "60%" }} />
          <span className="iot-szkielet" style={{ height: "0.875rem", width: "40%" }} />
        </span>
      </div>
      <div className="iot-ladowanie-karty__siatka">
        <span className="iot-szkielet" style={{ height: "7rem" }} />
        <span className="iot-szkielet" style={{ height: "7rem" }} />
      </div>
      <span className="iot-szkielet" style={{ height: "9rem" }} />
    </div>
  );
}

/* ---------- Pusto / błąd ---------- */
export interface StanProps {
  tytul: string;
  opis?: ReactNode;
  ikona?: KomponentIkony;
  akcje?: ReactNode;
  /** W ramce karty (gdy stoi samodzielnie w siatce) */
  wKarcie?: boolean;
  maly?: boolean;
}

function Stan({ tytul, opis, ikona: Ikona, akcje, wKarcie, maly, blad }: StanProps & { ikona: KomponentIkony; blad?: boolean }) {
  return (
    <div className={cx("iot-stan", blad && "iot-stan--blad", wKarcie && "iot-stan--karta", maly && "iot-stan--maly")} role={blad ? "alert" : undefined}>
      <span className="iot-stan__ikona"><Ikona /></span>
      <h3 className="iot-stan__tytul">{tytul}</h3>
      {opis && <p className="iot-stan__opis">{opis}</p>}
      {akcje && <div className="iot-grupa-btn">{akcje}</div>}
    </div>
  );
}

export function Pusto({ ikona = IkonaInfo, ...p }: StanProps) {
  return <Stan {...p} ikona={ikona} />;
}

export interface BladProps extends Omit<StanProps, "akcje"> {
  /** „Spróbuj ponownie” - ten sam pobieracz, który zawiódł (wzorzec/stany.html „Błąd wczytania”) */
  onPonow: () => void;
  /** „Zgłoś problem” (Serwis sprzętu); bez niego, gdy zgłoszenie nie ma sensu (brak serwera, mała karta) */
  zglos?: boolean;
}

export function Blad({ ikona = IkonaWykrzyknik, onPonow, zglos = true, ...p }: BladProps) {
  return (
    <Stan {...p} ikona={ikona} blad akcje={<>
      <Przycisk wariant="glowny" rozmiar={p.maly ? "maly" : undefined} ikona={IkonaOdswiez} onClick={onPonow}>{t("Spróbuj ponownie")}</Przycisk>
      {zglos && !p.maly && <PrzyciskLink do="/serwis-sprzetu" wariant="cichy">{t("Zgłoś problem")}</PrzyciskLink>}
    </>} />
  );
}

/* ---------- Urządzenie offline ---------- */
/** „Co sprawdzić?” przy urządzeniu offline - ścieżka pomocy asystenta (konfiguracja Wi-Fi; pulpity i lista urządzeń). */
export const CO_SPRAWDZIC = "/asystent?sciezka=wifi";

export interface UrzadzenieOfflineProps {
  nazwa: string;
  /** Np. „Brak połączenia od 14:32” - gotowy tekst z t() */
  odKiedy: string;
  ostatnieOdczyty?: ReadonlyArray<{ etykieta: string; wartosc: string }>;
  opis?: ReactNode;
  akcje?: ReactNode;
}

export function UrzadzenieOffline({ nazwa, odKiedy, ostatnieOdczyty, opis, akcje }: UrzadzenieOfflineProps) {
  return (
    <div className="iot-offline">
      <div className="iot-offline__naglowek">
        <span className="iot-offline__ikona"><IkonaWifiOff /></span>
        <span className="iot-rozpycha"><b>{nazwa}</b><span className="iot-cichy iot-maly" style={{ display: "block" }}>{odKiedy}</span></span>
        <span className="iot-pigulka iot-pigulka--wylaczona">{t("Offline")}</span>
      </div>
      {ostatnieOdczyty && ostatnieOdczyty.length > 0 && (
        <div className="iot-offline__odczyty">
          {ostatnieOdczyty.map((o) => (
            <span key={o.etykieta} className="iot-offline__odczyt"><small>{o.etykieta}</small><b className="iot-num">{o.wartosc}</b></span>
          ))}
        </div>
      )}
      {opis && <p className="iot-cichy iot-maly">{opis}</p>}
      {akcje && <div className="iot-grupa-btn">{akcje}</div>}
    </div>
  );
}

/** Pasek „brak internetu w telefonie” */
export function BrakSieci({ tekst, akcja }: { tekst: ReactNode; akcja?: ReactNode }) {
  return (
    <div className="iot-brak-sieci" role="status">
      <IkonaWifiOff />
      <span className="iot-rozpycha">{tekst}</span>
      {akcja}
    </div>
  );
}

/* ---------- Komunikaty ---------- */
export type RodzajKomunikatu = "ok" | "uwaga" | "alarm" | "info" | "zwykly";

const IKONY_KOMUNIKATU: Record<RodzajKomunikatu, KomponentIkony> = {
  ok: IkonaOkKolo, uwaga: IkonaUsterka, alarm: IkonaWykrzyknik, info: IkonaInfo, zwykly: IkonaInfo,
};

export interface KomunikatProps {
  rodzaj?: RodzajKomunikatu;
  tytul?: string;
  children?: ReactNode;
  ikona?: KomponentIkony;
  akcje?: ReactNode;
  className?: string;
}

export function Komunikat({ rodzaj = "info", tytul, children, ikona, akcje, className }: KomunikatProps) {
  const Ikona = ikona ?? IKONY_KOMUNIKATU[rodzaj];
  return (
    <div className={cx("iot-komunikat", rodzaj !== "zwykly" && `iot-komunikat--${rodzaj}`, className)} role={rodzaj === "alarm" ? "alert" : "status"}>
      <Ikona className="iot-komunikat__ikona" />
      <div className="iot-komunikat__tresc">
        {tytul && <b className="iot-komunikat__tytul">{tytul}</b>}
        {children}
        {akcje && <div className="iot-komunikat__akcje">{akcje}</div>}
      </div>
    </div>
  );
}

export interface TostProps {
  tekst: string;
  akcja?: { etykieta: string; onKliknij: () => void };
}

/** Krótki komunikat u dołu ekranu. Kiedy go pokazać i schować - decyduje aplikacja. */
export function Tost({ tekst, akcja }: TostProps) {
  return (
    <div className="iot-komunikat iot-komunikat--tost" role="status" aria-live="polite">
      <IkonaCheck className="iot-komunikat__ikona" />
      <span className="iot-rozpycha">{tekst}</span>
      {akcja && <button type="button" className="iot-btn iot-btn--cichy iot-btn--maly" onClick={akcja.onKliknij}>{akcja.etykieta}</button>}
    </div>
  );
}
